import classNames from 'classnames'
import Image from 'next/image'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Autoplay } from 'swiper/modules'
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react'

import { UserPointProps } from '@/features/web/components/point/Point'

import * as styles from './verticalRolloingMessages.css'

function VerticalRollingMessage(
  props: Omit<
    UserPointProps,
    | 'point'
    | 'mileage'
    | 'usableCouponCount'
    | 'pointRedDotList'
    | 'usableBizCouponCount'
    | 'bizCouponRedDotList'
    | 'poiVisitedCount'
  > & { className?: string }
) {
  const { grade, text, isEventOngoing, className } = props
  const swiperRef = useRef<SwiperRef | null>(null)
  const [slideIndex, setSlideIndex] = useState(0)
  const [gradeColor, setGradeColor] = useState({ bgColor: '', fontColor: '' })

  const messages = useMemo(() => {
    const messageList: (string | any)[] = []
    if (text) {
      messageList.push(text)
    }
    if (isEventOngoing) {
      messageList.push('이벤트중')
    }

    return messageList
  }, [text, isEventOngoing])

  const getGradeColor = useCallback(() => {
    switch (grade) {
    case 'BRONZE':
      setGradeColor({ bgColor: '#FFE4C3', fontColor: '#A27C37' })
      break
    case 'SILVER':
      setGradeColor({ bgColor: '#DFE4F3', fontColor: '#818BA9' })
      break
    case 'GOLD':
      setGradeColor({ bgColor: '#FFE9B3', fontColor: '#BB8606' })
      break
    case 'VIP':
      setGradeColor({ bgColor: '#2DCDC7,#3D8BFF,#976CFB', fontColor: '#FFFFFF' })
    }
  }, [grade])

  useEffect(() => {
    getGradeColor()
  }, [getGradeColor])

  useEffect(() => {
    const handleDisplaySizeChange = () => {
      const isLargeDisplay = window.innerWidth > 359
      const swiper = swiperRef.current?.swiper

      if (swiper) {
        if (isLargeDisplay) {
          // 스와이퍼가 display: none이 됐다가 다시 표출될 때 autoplay 작동 안 하는 이슈로 강제로 autoplay 업데이트
          swiper.autoplay.start()
          swiper.autoplay.paused = false
          swiper.autoplay.running = true
        } else {
          swiper.autoplay.stop()
        }
      }
    }

    handleDisplaySizeChange()
    window.addEventListener('resize', handleDisplaySizeChange)
    return () => window.removeEventListener('resize', handleDisplaySizeChange)
  }, [])

  return (
    gradeColor.bgColor &&
    gradeColor.fontColor &&
    grade && (
      <div className={`${styles.wrapper} ${styles.bgColorVariants[grade]}${className ? ` ${className}` : ''}`}>
        {messages && (
          <Swiper
            ref={swiperRef}
            modules={[Autoplay]}
            direction={'vertical'}
            centeredSlides={false}
            loop={messages.length > 1}
            allowTouchMove={false}
            speed={1000}
            autoplay={{
              delay: 1500,
            }}
            onSlideChange={(e) => {
              setSlideIndex(e.realIndex)
            }}
            onAutoplayPause={(swiper) => swiper.autoplay.start()}
            className={styles.swiper}
          >
            {messages.map((item, index) => {
              return (
                <SwiperSlide
                  key={index}
                  className={classNames(
                    styles.swiperSlide,
                    slideIndex === index ? styles.swiperSlideActive : undefined
                  )}
                  style={{
                    height: '20px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {typeof item === 'string' ? (
                    <p
                      className={classNames(
                        styles.textTag,
                        grade === 'VIP' ? styles.vipFontColor : ''
                      )}
                    >
                      {item}
                    </p>
                  ) : (
                    <Image
                      src={item}
                      alt={''}
                    />
                  )}
                </SwiperSlide>
              )
            })}
          </Swiper>
        )}
      </div>
    )
  )
}

export { VerticalRollingMessage }
